import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import PageTitle from "../../components/Common/PageTitle";
import {
  getAllInvoiceDetailSelector
} from "../../store/invoiceSlice";
import { getCompanyData } from "../../store/companySlice";
import {
  defaultInputSmStyle,
} from "../../constants/defaultStyles";
import gstService from "../../services/gstService";
import userService from "../../services/userService";

function GSTScreen() {
  const allInvoiceDetails = useSelector(getAllInvoiceDetailSelector);
  const companyData = useSelector(getCompanyData);

  // Authentication state - using Supabase auth state
  const [authState, setAuthState] = useState({ user: null, session: null, loading: true });

  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1); // Default to last month
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  // Selected invoices for GST filing
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filingType, setFilingType] = useState('GSTR1'); // 'GSTR1', 'GSTR3B', 'GSTR9', 'GSTR9C'
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(() => {
    // Default to current financial year
    const availableYears = gstService.getAvailableFinancialYears();
    return availableYears[0]?.value || new Date().getFullYear().toString();
  });
  const [annualSummary, setAnnualSummary] = useState(null);
  const [filingResult, setFilingResult] = useState(null);
  const [evcOtp, setEvcOtp] = useState('');
  const [isRequestingEvc, setIsRequestingEvc] = useState(false);

  // GST Portal Credentials State - Updated for Whitebook API
  const [gstCredentials, setGstCredentials] = useState({
    username: '', // GST_USERNAME for API
    password: '', // gst_pass for EVC OTP
    email: '', // email for API calls
    rememberMe: false
  });
  const [showGstPassword, setShowGstPassword] = useState(false);
  const [credentialsValidation, setCredentialsValidation] = useState(null);
  
  // Whitebook API Auth State
  const [whitebookAuth, setWhitebookAuth] = useState({
    txn: '', // Transaction ID from OTP request
    authToken: '', // Auth token from OTP verification
    tokenExpiry: null, // Token expiry time
    isAuthenticated: false
  });
  
  // OTP States for different flows
  const [authOtp, setAuthOtp] = useState(''); // Initial auth OTP
  const [isRequestingAuthOtp, setIsRequestingAuthOtp] = useState(false);
  const [isVerifyingAuthOtp, setIsVerifyingAuthOtp] = useState(false);

  // Check authentication status
  // Listen to Supabase auth state changes
  // Check authentication status
  // Uses userService which manages the application's authentication state
  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        // Use userService as the source of truth for authentication
        const currentUser = userService.getCurrentUser();

        // Validate the session to ensure it's still active
        // This is more robust than separate Supabase auth state as it aligns with App.js
        const isValid = await userService.validateSession();

        if (mounted) {
          if (isValid && currentUser) {
            setAuthState({
              user: currentUser,
              session: {
                access_token: userService.sessionToken,
                user: currentUser
              },
              loading: false
            });
          } else {
            // If validation fails, user might be logged out in another tab
            setAuthState({ user: null, session: null, loading: false });
          }
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        if (mounted) {
          setAuthState({ user: null, session: null, loading: false });
        }
      }
    };

    checkAuth();

    // Periodically re-validate session to handle expiration or logout in other tabs
    const intervalId = setInterval(checkAuth, 60000); // Check every minute

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  // Calculate annual summary when filing type or financial year changes
  useEffect(() => {
    if ((filingType === 'GSTR9' || filingType === 'GSTR9C') && allInvoiceDetails.length > 0) {
      const summary = gstService.calculateAnnualSummary(allInvoiceDetails, selectedFinancialYear);
      setAnnualSummary(summary);
      
      // Auto-select all invoices for the financial year
      const fyStart = new Date(`${selectedFinancialYear}-04-01`);
      const fyEnd = new Date(`${parseInt(selectedFinancialYear) + 1}-03-31`);
      
      const annualInvoiceIds = new Set(
        allInvoiceDetails
          .filter(invoice => {
            const invoiceDate = new Date(invoice.date);
            return invoiceDate >= fyStart && invoiceDate <= fyEnd;
          })
          .map(invoice => invoice.id)
      );
      
      setSelectedInvoices(annualInvoiceIds);
      setSelectAll(annualInvoiceIds.size === allInvoiceDetails.length);
    } else {
      setAnnualSummary(null);
    }
  }, [filingType, selectedFinancialYear, allInvoiceDetails]);

  // Filter states
  const [showOnlyPaid, setShowOnlyPaid] = useState(false);
  const [showOnlyGSTCollected, setShowOnlyGSTCollected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Filter invoices by date range and additional filters
  const filteredInvoices = useMemo(() => {
    if (!startDate || !endDate) return allInvoiceDetails;

    return allInvoiceDetails.filter(invoice => {
      const invoiceDate = new Date(invoice.createdDate);
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Set time to start/end of day for proper comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // Check if invoice is within date range
      const isInRange = invoiceDate >= start && invoiceDate <= end;
      
      // Apply additional filters
      if (showOnlyPaid && invoice.statusName !== 'Paid') return false;
      if (showOnlyGSTCollected && (!invoice.taxes || invoice.taxes.length === 0 || 
        invoice.taxes.reduce((sum, tax) => sum + (parseFloat(tax.amount) || 0), 0) <= 0)) return false;
      
      return isInRange;
    });
  }, [allInvoiceDetails, startDate, endDate, showOnlyPaid, showOnlyGSTCollected]);

  // Paginated invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredInvoices.slice(startIndex, endIndex);
  }, [filteredInvoices, currentPage, pageSize]);

  // Calculate totals for selected invoices
  const selectedInvoiceTotals = useMemo(() => {
    const selectedInvoiceData = filteredInvoices.filter(inv => selectedInvoices.has(inv.id));
    
    return selectedInvoiceData.reduce((totals, invoice) => {
      const invoiceAmount = parseFloat(invoice.totalAmount) || 0;
      // Calculate individual tax components using only the proven approach from gstTransformer.js
      let cgst = 0, sgst = 0, igst = 0;
      
      // Extract from taxes array using title field (same as gstTransformer.js)
      if (invoice.taxes && Array.isArray(invoice.taxes)) {
        invoice.taxes.forEach(tax => {
          const title = (tax.title || '').toLowerCase();
          const amount = parseFloat(tax.amount) || 0;

          if (title.includes('cgst')) {
            cgst += amount;
          } else if (title.includes('sgst')) {
            sgst += amount;
          } else if (title.includes('igst')) {
            igst += amount;
          }
        });
      }
      
      // Calculate total tax amount from individual components only
      const taxAmount = cgst + sgst + igst;
      
      return {
        count: totals.count + 1,
        totalAmount: totals.totalAmount + invoiceAmount,
        totalTaxAmount: totals.totalTaxAmount + taxAmount,
        totalCGST: totals.totalCGST + cgst,
        totalSGST: totals.totalSGST + sgst,
        totalIGST: totals.totalIGST + igst,
      };
    }, {
      count: 0,
      totalAmount: 0,
      totalTaxAmount: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
    });
  }, [filteredInvoices, selectedInvoices]);

  // Calculate totals for filtered invoices
  const filteredTotals = useMemo(() => {
    return filteredInvoices.reduce((totals, invoice) => {
      const amount = parseFloat(invoice.totalAmount) || 0;
      const isPaid = invoice.paymentStatus === 'PAID' || invoice.statusName === 'Paid';
      
      return {
        totalCollected: totals.totalCollected + (isPaid ? amount : 0),
        totalDue: totals.totalDue + (!isPaid ? amount : 0),
      };
    }, {
      totalCollected: 0,
      totalDue: 0,
    });
  }, [filteredInvoices]);

  // Enhanced validation helper with detailed feedback
  const validateFilingInputs = useCallback(() => {
    // Validate company GSTIN
    if (!companyData?.companyGST) {
      toast.error('Company GSTIN missing. Please update your company profile first.');
      return false;
    }

    // Validate GST portal credentials (required for filing authentication)
    const credentialValidation = gstService.validateGstCredentials(
      gstCredentials.username,
      gstCredentials.password
    );

    if (!credentialValidation.valid) {
      toast.error(`GST Credentials: ${credentialValidation.error}`);
      setCredentialsValidation(credentialValidation);
      return false;
    }

    // Validate EVC OTP format
    if (!evcOtp || evcOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit EVC OTP');

      // Show guidance for getting EVC OTP
      setTimeout(() => {
        toast.info('💡 Click "Get EVC OTP" button to request OTP using your GST credentials', {
          autoClose: 6000,
          position: 'bottom-right'
        });
      }, 1000);
      return false;
    }

    // Validate invoice selection
    if (selectedInvoices.size === 0) {
      toast.error('Please select at least one invoice to file');
      return false;
    }

    // Validate date range
    if (!startDate || !endDate) {
      toast.error('Please select a valid date range for filing');
      return false;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) {
      toast.error('Start date cannot be after end date');
      return false;
    }

    // Additional validation for large date ranges
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 90) {
      toast.warning('Filing period exceeds 90 days. Consider splitting into smaller periods for better processing.');
    }

    return true;
  }, [companyData?.companyGST, evcOtp, selectedInvoices.size, startDate, endDate, gstCredentials.username, gstCredentials.password]);

  // Handle Authentication OTP input changes
  const handleAuthOtpChange = useCallback((index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    setAuthOtp(prev => {
      const otpArray = prev.split('');
      otpArray[index] = value;
      return otpArray.join('').substring(0, 6); // Ensure max 6 digits
    });
  }, []);

  // Handle EVC OTP input changes
  const handleEvcOtpChange = useCallback((index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    setEvcOtp(prev => {
      const otpArray = prev.split('');
      otpArray[index] = value;
      return otpArray.join('').substring(0, 6); // Ensure max 6 digits
    });
  }, []);

  // Update selectAll state when filtered invoices or selected invoices change
  useEffect(() => {
    if (filteredInvoices.length === 0) {
      setSelectAll(false);
    } else {
      const allFilteredSelected = filteredInvoices.every(invoice => selectedInvoices.has(invoice.id));
      setSelectAll(allFilteredSelected && selectedInvoices.size > 0);
    }
  }, [filteredInvoices, selectedInvoices]);

  // Handle initial authentication OTP request
  const handleAuthOtpRequest = useCallback(async () => {
    if (!gstCredentials.email) {
      toast.error('Email is required for authentication.');
      return;
    }
    
    setIsRequestingAuthOtp(true);
    
    try {
      const result = await gstService.requestAuthOtp(gstCredentials.email);
      
      if (result.success) {
        setWhitebookAuth(prev => ({ ...prev, txn: result.txn }));
        toast.success('OTP sent to your registered mobile and email. Please enter the 6-digit OTP.');
      } else {
        toast.error(`Failed to send OTP: ${result.error}`);
      }
    } catch (error) {
      console.error('Auth OTP request error:', error);
      toast.error('Failed to request OTP. Please try again.');
    } finally {
      setIsRequestingAuthOtp(false);
    }
  }, [gstCredentials.email]);
  
  // Handle OTP verification
  const handleAuthOtpVerify = useCallback(async () => {
    if (!authOtp || authOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP.');
      return;
    }
    
    if (!whitebookAuth.txn) {
      toast.error('Please request OTP first.');
      return;
    }
    
    setIsVerifyingAuthOtp(true);
    
    try {
      const result = await gstService.verifyAuthOtp(gstCredentials.email, authOtp, whitebookAuth.txn);
      
      if (result.success) {
        setWhitebookAuth({
          txn: whitebookAuth.txn,
          authToken: result.authToken,
          tokenExpiry: result.expiryTime,
          isAuthenticated: true
        });
        toast.success('Authentication successful! You can now proceed with GST operations.');
      } else {
        toast.error(`OTP verification failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Auth OTP verification error:', error);
      toast.error('OTP verification failed. Please try again.');
    } finally {
      setIsVerifyingAuthOtp(false);
    }
  }, [authOtp, whitebookAuth.txn, gstCredentials.email]);

  // Handle EVC OTP request (for filing)
  const handleEvcOtpRequest = useCallback(async () => {
    if (!whitebookAuth.email) {
      toast.error('Email is required for EVC OTP request.');
      return;
    }
    
    if (!whitebookAuth.isAuthenticated || !whitebookAuth.authToken) {
      toast.error('Please complete authentication first.');
      return;
    }

    if (!gstCredentials.password) {
      toast.error('GST portal password is required for EVC OTP.');
      return;
    }

    setIsRequestingEvc(true);
    
    try {
      const result = await gstService.requestEvcOtp(
        whitebookAuth.email,
        gstCredentials.password
      );

      if (result.success) {
        toast.success('EVC OTP sent successfully! Check your registered mobile.');
      } else {
        toast.error(`Failed to send EVC OTP: ${result.error}`);
      }
    } catch (error) {
      console.error('EVC OTP request error:', error);
      toast.error('Failed to request EVC OTP. Please try again.');
    } finally {
      setIsRequestingEvc(false);
    }
  }, [whitebookAuth.email, gstCredentials.password, whitebookAuth.isAuthenticated, whitebookAuth.authToken]);

  // Handle select all invoices
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedInvoices(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filteredInvoices.map(inv => inv.id));
      setSelectedInvoices(allIds);
      setSelectAll(true);
    }
  }, [selectAll, filteredInvoices]);

  // Handle individual invoice selection
  const handleInvoiceSelect = useCallback((invoiceId) => {
    setSelectedInvoices(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(invoiceId)) {
        newSelection.delete(invoiceId);
      } else {
        newSelection.add(invoiceId);
      }
      
      // Update select all state - check if all filtered invoices are selected
      setSelectAll(newSelection.size === filteredInvoices.length && filteredInvoices.length > 0);
      
      return newSelection;
    });
  }, [filteredInvoices.length]);

  // Pagination helpers
  const goToPrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);



  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Enhanced automated GST filing with comprehensive error handling and annual returns support
  const handleFileGST = useCallback(async () => {
    // Validate inputs with enhanced feedback
    if (!validateFilingInputs()) {
      return;
    }

    const companyGSTIN = companyData?.companyGST;
    setIsProcessing(true);
    setFilingResult(null);

    // Determine if this is annual returns filing
    const isAnnualReturn = filingType === 'GSTR9' || filingType === 'GSTR9C';

    // Progress notification
    toast.info(`🚀 Starting automated ${filingType} filing process...`, {
      autoClose: 3000
    });

    try {
      let result;

      if (isAnnualReturn) {
        // Handle annual returns filing
        const selectedInvoiceData = filteredInvoices.filter(inv =>
          selectedInvoices.has(inv.id)
        );

        if (filingType === 'GSTR9') {
          // Calculate annual summary for GSTR-9
          const summary = gstService.calculateAnnualSummary(selectedInvoiceData, selectedFinancialYear);
          setAnnualSummary(summary);

          // First save the GSTR-9 data
          const saveResult = await gstService.saveGSTR9(companyGSTIN, selectedFinancialYear, {
            gstin: companyGSTIN,
            financialYear: selectedFinancialYear,
            totalTurnover: summary.totalTurnover,
            transportTurnover: summary.transportTurnover,
            courierTurnover: summary.courierTurnover,
            totalTaxPaid: summary.totalTaxPaid,
            totalITCAvailed: summary.totalITCAvailed
          });

          // Then file it with OTP
          if (saveResult.success) {
            result = await gstService.fileGSTR9(companyGSTIN, selectedFinancialYear, evcOtp, companyData?.companyPAN);
          } else {
            result = saveResult;
          }

        } else if (filingType === 'GSTR9C') {
          // Handle GSTR-9C with audit data
          const summary = gstService.calculateAnnualSummary(selectedInvoiceData, selectedFinancialYear);
          
          if (!summary.requiresAudit) {
            toast.warning('GSTR-9C is only required for companies with turnover > ₹2 crores. Consider filing GSTR-9 instead.');
            setIsProcessing(false);
            return;
          }

          setAnnualSummary(summary);

          // For GSTR-9C, we need additional audit data
          const auditData = {
            auditedTurnover: summary.totalTurnover,
            booksTurnover: summary.totalTurnover, // Assuming books match for demo
            auditedTax: {
              iamt: Math.floor(summary.totalTaxPaid * 0.6),
              camt: Math.floor(summary.totalTaxPaid * 0.2),
              samt: Math.floor(summary.totalTaxPaid * 0.2)
            },
            booksTax: {
              iamt: Math.floor(summary.totalTaxPaid * 0.6),
              camt: Math.floor(summary.totalTaxPaid * 0.2),
              samt: Math.floor(summary.totalTaxPaid * 0.2)
            },
            auditedITC: {
              iamt: Math.floor(summary.totalITCAvailed * 0.6),
              camt: Math.floor(summary.totalITCAvailed * 0.2),
              samt: Math.floor(summary.totalITCAvailed * 0.2)
            },
            booksITC: {
              iamt: Math.floor(summary.totalITCAvailed * 0.6),
              camt: Math.floor(summary.totalITCAvailed * 0.2),
              samt: Math.floor(summary.totalITCAvailed * 0.2)
            },
            auditorName: "CA Transport Auditor",
            auditorPan: "ABCDE1234F",
            auditDate: new Date().toLocaleDateString('en-GB'),
            auditPlace: "Delhi",
            transportTurnover: summary.transportTurnover,
            courierTurnover: summary.courierTurnover
          };

          // Save GSTR-9C
          const saveResult = await gstService.saveGSTR9C(companyGSTIN, selectedFinancialYear, auditData);

          // Then file it
          if (saveResult.success) {
            result = await gstService.fileGSTR9C(companyGSTIN, selectedFinancialYear, evcOtp, companyData?.companyPAN);
          } else {
            result = saveResult;
          }
        }
      } else {
        // Handle monthly returns (GSTR-1, GSTR-3B)
        const selectedInvoiceData = filteredInvoices.filter(inv =>
          selectedInvoices.has(inv.id)
        );

        result = await gstService.automatedFiling(
          filingType,
          selectedInvoiceData,
          companyGSTIN,
          startDate,
          endDate,
          evcOtp
        );
      }

      if (result.success) {
        setFilingResult(result);

        // Enhanced success notification
        const successMsg = `🎉 ${result.filingType || filingType} filed successfully!`;
        const detailMsg = isAnnualReturn 
          ? `ARN: ${result.arn || 'Pending'} | Financial Year: ${selectedFinancialYear}`
          : `ARN: ${result.arn || 'Pending'} | ${result.invoiceCount || selectedInvoices.size} invoice(s) processed`;

        toast.success(`${successMsg}\n${detailMsg}`, {
          autoClose: 12000
        });

        // Show automation details
        if (result.automation) {
          setTimeout(() => {
            const automationFeatures = [];
            if (result.automation.evcUsed) automationFeatures.push('✅ EVC automated');
            if (result.automation.sessionManaged) automationFeatures.push('✅ Session managed');
            if (result.automation.validationPassed) automationFeatures.push('✅ Validation passed');

            toast.info(`🤖 Automation: ${automationFeatures.join(', ')}`, {
              autoClose: 6000,
              position: 'bottom-right'
            });
          }, 2000);
        }

        // Show filing efficiency info
        if (result.rateHint === 'reused_session') {
          toast.info('⚡ Efficient filing - existing session reused!', {
            autoClose: 5000,
            position: 'bottom-left'
          });
        } else if (result.rateHint === 'new_session_auto') {
          toast.info('🔄 New session created automatically for filing', {
            autoClose: 5000,
            position: 'bottom-left'
          });
        }

        // Clear selections and reset form
        setSelectedInvoices(new Set());
        setSelectAll(false);
        setEvcOtp('');

      } else {
        const errorMsg = result.error || 'Filing failed';
        console.error('GST Filing Error:', result);

        // Enhanced error handling with specific guidance
        if (errorMsg.includes('session')) {
          toast.error(`🔐 Session Error: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Session will be auto-recreated on next attempt', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('EVC') || errorMsg.includes('OTP')) {
          toast.error(`🔢 EVC OTP Error: ${errorMsg}`, { autoClose: 8000 });
          setEvcOtp(''); // Clear invalid OTP
          toast.info('💡 Please request a new EVC OTP and try again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('rate') || errorMsg.includes('limit')) {
          toast.error(`⚠️ Rate Limit: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Please wait a few minutes before trying again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('validation')) {
          toast.error(`📋 Validation Error: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Please check your invoice data and try again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else {
          toast.error(`❌ Filing Error: ${errorMsg}`, { autoClose: 8000 });
        }
      }

    } catch (error) {
      console.error("GST filing error:", error);

      // Comprehensive error handling with user guidance
      const errorMessage = error.message || "Failed to file GST. Please try again.";

      if (errorMessage.includes('Unauthorized') || errorMessage.includes('not authenticated')) {
        toast.error('🔐 Authentication expired. Please refresh and try again.', {
          autoClose: 8000
        });
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error('🌐 Network error. Please check your connection and try again.', {
          autoClose: 8000
        });
        toast.info('💡 Check your internet connection and retry', {
          autoClose: 5000,
          position: 'bottom-right'
        });
      } else if (errorMessage.includes('Invalid GSTIN')) {
        toast.error('📝 Invalid company GSTIN. Please update your company profile.', {
          autoClose: 8000
        });
      } else if (errorMessage.includes('No invoices')) {
        toast.error('📋 No invoices selected. Please select invoices to file.', {
          autoClose: 6000
        });
      } else {
        toast.error(`❌ Unexpected error: ${errorMessage}`, {
          autoClose: 8000
        });

        // Offer general troubleshooting
        setTimeout(() => {
          toast.info('💡 Try refreshing the page or contact support if issue persists', {
            autoClose: 8000,
            position: 'bottom-right'
          });
        }, 2000);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [selectedInvoices, filteredInvoices, companyData, startDate, endDate, filingType, selectedFinancialYear, evcOtp, validateFilingInputs]);

  // Handle date range preset
  const handleDateRangePreset = useCallback((preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        if (lastQuarter < 0) {
          start = new Date(today.getFullYear() - 1, 9, 1); // Q4 of previous year
          end = new Date(today.getFullYear() - 1, 11, 31);
        } else {
          start = new Date(today.getFullYear(), lastQuarter * 3, 1);
          end = new Date(today.getFullYear(), (lastQuarter + 1) * 3, 0);
        }
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  // Show loading while checking authentication
  if (authState.loading) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-xl p-6">
          <PageTitle title="GST Filing" />
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading GST services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-white rounded-xl p-6">
        <PageTitle title="GST Filing" />

        {/* Date Range Filter */}
        <div className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Date Filtering Active
            </div>
          </div>

          <div className="bg-white border border-blue-200 rounded-lg p-4">
            {/* Date Range Presets */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleDateRangePreset('thisMonth')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                This Month
              </button>
              <button
                onClick={() => handleDateRangePreset('lastMonth')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                Last Month
              </button>
              <button
                onClick={() => handleDateRangePreset('thisQuarter')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                This Quarter
              </button>
              <button
                onClick={() => handleDateRangePreset('lastQuarter')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                Last Quarter
              </button>
            </div>

            {/* Custom Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={defaultInputSmStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={defaultInputSmStyle}
                />
              </div>
            </div>

            {/* Filtered Totals Preview */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-800">Total Collected</h4>
                <div className="text-2xl font-bold text-green-900">
                  <NumberFormat
                    value={filteredTotals.totalCollected}
                    displayType="text"
                    thousandSeparator
                    prefix="₹"
                  />
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-800">Total Due</h4>
                <div className="text-2xl font-bold text-yellow-900">
                  <NumberFormat
                    value={filteredTotals.totalDue}
                    displayType="text"
                    thousandSeparator
                    prefix="₹"
                  />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800">Total Invoices</h4>
                <div className="text-2xl font-bold text-blue-900">
                  {filteredInvoices.length}
                </div>
              </div>
            </div>

            {/* Invoice Filters */}
            <div className="mt-6 border-t border-blue-100 pt-4 pb-4">
              <div className="flex items-center gap-6">
                <label className="flex items-center text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyPaid}
                    onChange={(e) => setShowOnlyPaid(e.target.checked)}
                    className="rounded border-gray-300 mr-2"
                  />
                  <span className="text-gray-700">Show only Paid</span>
                </label>
                <label className="flex items-center text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyGSTCollected}
                    onChange={(e) => setShowOnlyGSTCollected(e.target.checked)}
                    className="rounded border-gray-300 mr-2"
                  />
                  <span className="text-gray-700">Show only GST Collected</span>
                </label>
              </div>
            </div>

            {/* Invoice Table */}
            <div className="-mx-4 -mb-6">
              <table className="w-full bg-white border border-gray-200">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left py-3 px-4">Invoice No</th>
                    <th className="text-left py-3 px-4">Client</th>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Tax Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice) => {
                    const taxAmount = invoice.taxes?.reduce((sum, tax) =>
                      sum + (parseFloat(tax.amount || tax.value) || 0), 0
                    ) || parseFloat(invoice.totalTaxAmount || invoice.tax_amount || invoice.taxAmount) || 
                    (parseFloat(invoice.cgst || 0) + parseFloat(invoice.sgst || 0) + parseFloat(invoice.igst || 0)) || 0;

                    return (
                      <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedInvoices.has(invoice.id)}
                            onChange={() => handleInvoiceSelect(invoice.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="py-3 px-4">
                          {invoice.invoiceNumber || invoice.invoiceNo || invoice.invoice_number || invoice.number || `INV-${invoice.id}` || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          {invoice.clientDetail?.name || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          {new Date(invoice.createdDate).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            invoice.statusName === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : invoice.statusName === 'Unpaid'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.statusName || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <NumberFormat
                            value={invoice.totalAmount || 0}
                            displayType="text"
                            thousandSeparator
                            prefix="₹"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <NumberFormat
                            value={taxAmount}
                            displayType="text"
                            thousandSeparator
                            prefix="₹"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors whitespace-nowrap"
                  >
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>

                  <label className="flex items-center text-sm text-gray-700 gap-2">
                    Rows
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[5, 10, 25, 50].map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, filteredInvoices.length)} to {Math.min(currentPage * pageSize, filteredInvoices.length)} of {filteredInvoices.length} invoices
                  </div>
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {Math.ceil(filteredInvoices.length / pageSize)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredInvoices.length / pageSize), prev + 1))}
                    disabled={currentPage >= Math.ceil(filteredInvoices.length / pageSize)}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Selected Invoices Summary */}
              {selectedInvoices.size > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mx-4 mt-6 mb-6">
                  <h4 className="text-lg font-semibold text-indigo-900 mb-3">
                    Selected for GST Filing ({selectedInvoiceTotals.count} invoices)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-indigo-700">Total Amount:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalAmount}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">Total Tax:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalTaxAmount}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">CGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalCGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">SGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalSGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">IGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalIGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Smart GST Filing */}
        <div className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">Smart GST Filing</h3>
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Automated Filing Ready
              </div>
            </div>

            {/* Filing For Section */}
            <div className="bg-white border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-blue-600 text-sm font-medium">Filing For:</span>
                <span className="text-gray-900 font-semibold">
                  {companyData?.companyGST || companyData?.GST || 'GST Number not set'}
                </span>
                <span className="text-sm text-gray-600">
                  ({companyData?.companyName || 'Company Name'})
                </span>
              </div>
            </div>

            {/* Whitebook API Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address (for Whitebook API)
              </label>
              <input
                type="email"
                value={gstCredentials.email}
                onChange={(e) => setGstCredentials(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email for API authentication"
                required
              />
            </div>

            {/* GST Portal Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Portal Username
                </label>
                <input
                  type="text"
                  value={gstCredentials.username}
                  onChange={(e) => setGstCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter GST portal username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Portal Password
                </label>
                <div className="relative">
                  <input
                    type={showGstPassword ? 'text' : 'password'}
                    value={gstCredentials.password}
                    onChange={(e) => setGstCredentials(prev => ({ ...prev, password: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      credentialsValidation && !credentialsValidation.valid
                        ? 'border-red-300'
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter GST portal password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGstPassword(!showGstPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showGstPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            {/* Validation Error Display */}
            {credentialsValidation && !credentialsValidation.valid && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                ❌ {credentialsValidation.error}
              </div>
            )}

            {/* Whitebook Authentication Section */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">🔐 Whitebook API Authentication</h4>
              
              {!whitebookAuth.isAuthenticated ? (
                <div className="space-y-3">
                  {/* Auth OTP Section */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Authentication OTP (6 digits)
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {[...Array(6)].map((_, index) => (
                            <input
                              key={index}
                              type="text"
                              maxLength="1"
                              value={authOtp[index] || ''}
                              onChange={(e) => handleAuthOtpChange(index, e.target.value)}
                              className={`w-8 h-8 text-center border rounded text-sm font-medium ${
                                authOtp[index] ? 'border-green-400 bg-green-50' : 'border-gray-300'
                              }`}
                              disabled={!whitebookAuth.otpRequested}
                            />
                          ))}
                        </div>
                        <span className="ml-2">{authOtp.length}/6</span>
                        {authOtp.length === 6 && (
                          <span className="ml-2 text-green-600">✓ Ready</span>
                        )}
                      </div>
                    </div>

                    <div>
                      {!whitebookAuth.otpRequested ? (
                        <button
                          onClick={handleAuthOtpRequest}
                          disabled={!gstCredentials.email || isRequestingAuthOtp}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        >
                          {isRequestingAuthOtp ? (
                            <>
                              <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                              Sending...
                            </>
                          ) : (
                            <>📧 Request Auth OTP</>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={handleAuthOtpVerify}
                          disabled={authOtp.length !== 6 || isVerifyingAuthOtp}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        >
                          {isVerifyingAuthOtp ? (
                            <>
                              <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                              Verifying...
                            </>
                          ) : (
                            <>✅ Verify OTP</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-green-700">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="font-medium">Authenticated successfully</span>
                  <span className="text-sm text-gray-600">
                    Token expires: {new Date(whitebookAuth.tokenExpiry).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* EVC OTP Section - Only show when authenticated */}
            {whitebookAuth.isAuthenticated && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EVC OTP (6 digits)
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        value={evcOtp[index] || ''}
                        onChange={(e) => handleEvcOtpChange(index, e.target.value)}
                        className={`w-8 h-8 text-center border rounded text-sm font-medium ${
                          evcOtp[index] ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-2">{evcOtp.length}/6</span>
                  {evcOtp.length === 6 && (
                    <span className="ml-2 text-green-600">✓ Ready</span>
                  )}
                </div>
              </div>

              {/* Get EVC OTP Button */}
              <div>
                <button
                  onClick={handleEvcOtpRequest}
                  disabled={!whitebookAuth.isAuthenticated || 
                    isRequestingEvc ||
                    !gstCredentials.password}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  title={
                    !whitebookAuth.isAuthenticated
                      ? "Complete authentication first"
                      : !gstCredentials.password
                      ? "Enter GST portal password"
                      : "Request EVC OTP for filing"
                  }
                >
                  {isRequestingEvc ? (
                    <>
                      <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      🔐 Get EVC OTP
                    </>
                  )}
                </button>
              </div>
            </div>
            )}

            {/* Filing Section */}
            <div className="space-y-4">
              {/* Filing Type and Financial Year Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filing Type
                  </label>
                  <select
                    value={filingType}
                    onChange={(e) => setFilingType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <optgroup label="Monthly Returns">
                      <option value="GSTR1">GSTR-1</option>
                      <option value="GSTR3B">GSTR-3B</option>
                    </optgroup>
                    <optgroup label="Annual Returns">
                      <option value="GSTR9">GSTR-9 (Annual Return)</option>
                      <option value="GSTR9C">GSTR-9C (Audit Return)</option>
                    </optgroup>
                  </select>
                </div>

                {/* Financial Year Selector - Only for Annual Returns */}
                {(filingType === 'GSTR9' || filingType === 'GSTR9C') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Financial Year
                    </label>
                    <select
                      value={selectedFinancialYear}
                      onChange={(e) => setSelectedFinancialYear(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {gstService.getAvailableFinancialYears().map(fy => (
                        <option key={fy.value} value={fy.value}>
                          {fy.label} ({fy.period})
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">
                      Select the financial year for annual return filing
                    </div>
                  </div>
                )}
              </div>

              {/* Annual Return Summary - Only for Annual Returns */}
              {(filingType === 'GSTR9' || filingType === 'GSTR9C') && annualSummary && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    📊 Annual Return Summary for FY {annualSummary.financialYear}-{parseInt(annualSummary.financialYear) + 1}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-gray-600">Total Invoices</div>
                      <div className="font-medium text-blue-900">{annualSummary.totalInvoices}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Total Turnover</div>
                      <div className="font-medium text-blue-900">₹{(annualSummary.totalTurnover / 100000).toFixed(1)}L</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Tax Paid</div>
                      <div className="font-medium text-blue-900">₹{(annualSummary.totalTaxPaid / 100000).toFixed(1)}L</div>
                    </div>
                    <div>
                      <div className="text-gray-600">ITC Availed</div>
                      <div className="font-medium text-blue-900">₹{(annualSummary.totalITCAvailed / 100000).toFixed(1)}L</div>
                    </div>
                  </div>
                  {filingType === 'GSTR9C' && (
                    <div className="mt-2 text-xs text-blue-700">
                      ⚠️ GSTR-9C is required as annual turnover exceeds ₹2 crores. Audit certificate will be included.
                    </div>
                  )}
                </div>
              )}

              {/* Filing Button */}
              <div>
                <button
                  onClick={handleFileGST}
                  disabled={isProcessing || selectedInvoices.size === 0 || !evcOtp || evcOtp.length !== 6}
                  className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-md font-medium hover:from-green-700 hover:to-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin w-4 h-4 border border-white border-t-transparent rounded-full"></div>
                      Filing in Progress...
                    </>
                  ) : (
                    <>
                      {filingType === 'GSTR9' || filingType === 'GSTR9C' 
                        ? `📋 File ${filingType} for FY ${selectedFinancialYear}-${parseInt(selectedFinancialYear) + 1} (${selectedInvoices.size} invoices)`
                        : `🚀 File ${filingType} Return (${selectedInvoices.size} selected)`
                      }
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Filing Result Display */}
            {filingResult && (
              <div className={`p-3 rounded-md border ${filingResult.success
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                <div className="font-medium mb-1">
                  {filingResult.success ? '✅ Filing Successful' : '❌ Filing Failed'}
                </div>
                <div className="text-sm space-y-1">
                  {filingResult.success ? (
                    <>
                      <div>ARN: {filingResult.arn || 'Pending'} | Status: Filed</div>
                      {(filingResult.returnType === 'GSTR9' || filingResult.returnType === 'GSTR9C') && (
                        <div>
                          Return Type: {filingResult.returnType} | 
                          Financial Year: {filingResult.retPeriod || selectedFinancialYear}-{parseInt(filingResult.retPeriod || selectedFinancialYear) + 1}
                        </div>
                      )}
                      {filingResult.returnType === 'GSTR9C' && (
                        <div className="text-xs text-green-700 mt-1">
                          📋 Audit certificate included with annual return filing
                        </div>
                      )}
                    </>
                  ) : (
                    <div>Error: {filingResult.error}</div>
                  )}
                </div>
              </div>
            )}



            {/* Security Notice */}
            <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-md">
              <span className="text-blue-600">🔒</span>
              <div className="text-blue-700">
                <strong>Security:</strong> Your credentials are sent directly to GST portal through our secure API.
                We do not store or log your password anywhere in our systems.
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default GSTScreen;